import dJSON from 'dirty-json';

import Splide from '@splidejs/splide';

const moduleSelector = '.testimonials-1';
const lightboxSelector = '.testimonials-1__lightbox';
const lightboxActiveClass = 'is-active';
const bodyLightboxClass = 'testimonials-1__lightbox-open';

const lightboxState = {
  element: null,
  lastFocused: null,
  hasKeyListener: false,
};

const closeLightbox = () => {
  if (!lightboxState.element) return;

  lightboxState.element.classList.remove(lightboxActiveClass);
  lightboxState.element.setAttribute('aria-hidden', 'true');
  document.body.classList.remove(bodyLightboxClass);

  if (lightboxState.lastFocused?.focus) {
    lightboxState.lastFocused.focus();
  }
};

const ensureLightbox = () => {
  if (lightboxState.element) return lightboxState.element;

  const existingLightbox = document.querySelector(lightboxSelector);
  if (existingLightbox) {
    lightboxState.element = existingLightbox;
    return existingLightbox;
  }

  const lightbox = document.createElement('div');
  lightbox.className = lightboxSelector.replace('.', '');
  lightbox.setAttribute('aria-hidden', 'true');
  lightbox.innerHTML = `
    <div class="testimonials-1__lightbox__backdrop" data-lightbox-close></div>
    <div class="testimonials-1__lightbox__dialog" role="dialog" aria-modal="true" aria-label="Testimonial">
      <button class="testimonials-1__lightbox__close" type="button" aria-label="Close testimonial" data-lightbox-close>&times;</button>
      <div class="testimonials-1__lightbox__stars"></div>
      <h2 class="testimonials-1__lightbox__title"></h2>
      <p class="testimonials-1__lightbox__text"></p>
      <p class="testimonials-1__lightbox__author"></p>
    </div>
  `;

  document.body.appendChild(lightbox);

  lightbox.addEventListener('click', (event) => {
    if (event.target.closest('[data-lightbox-close]')) {
      closeLightbox();
    }
  });

  if (!lightboxState.hasKeyListener) {
    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape'
        && lightboxState.element?.classList.contains(lightboxActiveClass)
      ) {
        closeLightbox();
      }
    });
    lightboxState.hasKeyListener = true;
  }

  lightboxState.element = lightbox;
  return lightbox;
};

const openLightbox = ({
  starsHtml,
  title,
  text,
  author,
}) => {
  const lightbox = ensureLightbox();
  if (!lightbox) return;

  lightboxState.lastFocused = document.activeElement;

  const starsEl = lightbox.querySelector('.testimonials-1__lightbox__stars');
  const titleEl = lightbox.querySelector('.testimonials-1__lightbox__title');
  const textEl = lightbox.querySelector('.testimonials-1__lightbox__text');
  const authorEl = lightbox.querySelector('.testimonials-1__lightbox__author');

  if (starsEl) starsEl.innerHTML = starsHtml || '';
  if (titleEl) titleEl.textContent = title || '';
  if (textEl) textEl.textContent = text || '';
  if (authorEl) authorEl.textContent = author || '';

  lightbox.classList.add(lightboxActiveClass);
  lightbox.setAttribute('aria-hidden', 'false');
  document.body.classList.add(bodyLightboxClass);

  lightbox.querySelector('.testimonials-1__lightbox__close')?.focus();
};

const getCardData = (card) => {
  const starsEl = card.querySelector('.testimonials-1__testimonial__stars');
  const titleEl = card.querySelector('.testimonials-1__testimonial__title');
  const textEl = card.querySelector('.testimonials-1__testimonial__text');
  const authorEl = card.querySelector('.testimonials-1__testimonial__author');

  return {
    starsHtml: starsEl?.innerHTML || '',
    title: titleEl?.textContent || '',
    text: textEl?.textContent || '',
    author: authorEl?.textContent || '',
  };
};

const updateClampedState = (moduleEl) => {
  moduleEl.querySelectorAll('.testimonials-1__testimonial').forEach((card) => {
    const textEl = card.querySelector('.testimonials-1__testimonial__text');
    if (!textEl) return;

    const isTruncated = textEl.scrollHeight > textEl.clientHeight + 1;
    card.classList.toggle('is-truncated', isTruncated);

    if (isTruncated) {
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', 'Open full testimonial');
    } else {
      card.removeAttribute('tabindex');
      card.removeAttribute('role');
      card.removeAttribute('aria-label');
    }
  });
};

let hasResizeListener = false;

const setupTestimonialsInteractions = (moduleEl) => {
  if (!moduleEl || moduleEl.dataset.lightboxInit === 'true') return;
  moduleEl.dataset.lightboxInit = 'true';

  const handleOpen = (card) => {
    if (!card?.classList.contains('is-truncated')) return;
    if (card.closest('.splide')?.classList.contains('is-dragging')) return;
    openLightbox(getCardData(card));
  };

  moduleEl.addEventListener('click', (event) => {
    const card = event.target.closest('.testimonials-1__testimonial');
    if (card && moduleEl.contains(card)) {
      handleOpen(card);
    }
  });

  moduleEl.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const card = event.target.closest('.testimonials-1__testimonial');
    if (card && moduleEl.contains(card)) {
      event.preventDefault();
      handleOpen(card);
    }
  });

  requestAnimationFrame(() => updateClampedState(moduleEl));

  if (!hasResizeListener) {
    window.addEventListener('resize', () => {
      requestAnimationFrame(() => {
        document.querySelectorAll(moduleSelector).forEach((moduleItem) => {
          updateClampedState(moduleItem);
        });
      });
    });
    hasResizeListener = true;
  }
};

/**
 * Module Init
 */
(() => {
  const modules = document.querySelectorAll(moduleSelector);
  if (!modules.length) return;

  modules.forEach((query) => {
    try {
      query.querySelector('script')?.remove(); // cleanup

      const { data } = dJSON.parse(query.data);

      const generateStars = (count) => {
        const starSvg = '<svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.6198 6.65283C16.9442 5.88078 18.0561 5.88073 18.3805 6.65283L18.3815 6.65479L21.1373 13.1587L21.2555 13.437L21.5553 13.4624L28.5992 14.0601H28.6022C29.4402 14.1268 29.7896 15.1785 29.1461 15.7358H29.1451L23.7936 20.3735L23.5651 20.5708L23.6334 20.8647L25.2379 27.7485C25.4293 28.5737 24.5389 29.2222 23.8112 28.7817L17.7584 25.1216L17.5006 24.9653L17.2418 25.1206L11.1901 28.7671H11.1891C10.4613 29.2076 9.5708 28.5591 9.76233 27.7339L11.3658 20.8501L11.4342 20.5562L11.2067 20.3589L5.85413 15.7212C5.2105 15.1638 5.55989 14.1121 6.39807 14.0454H6.401L13.444 13.4478L13.7448 13.4224L13.8619 13.145L16.6188 6.65479L16.6198 6.65283Z" fill="#7AAF42" stroke="#7AAF42"/></svg>';
        return starSvg.repeat(count);
      };

      data.forEach((item) => {
        query.querySelector('.splide__list').insertAdjacentHTML('beforeend', /* html */ `
          <div class="splide__slide">
            <div class="testimonials-1__testimonial">
              <div class="testimonials-1__testimonial__stars">
                ${generateStars(item.stars)}
              </div>
              <div class="testimonials-1__testimonial__content-wrapper">
                <h2 class="testimonials-1__testimonial__title">${item.title || ''}</h2>
                <p class="testimonials-1__testimonial__text">${item.text || ''}</p>
              </div>
              <p class="testimonials-1__testimonial__author">${item.author || ''}</p>
            </div>
          </div>
        `);
      });

      setupTestimonialsInteractions(query);
    } catch (error) {
      console.error(`Error: ${moduleSelector}`, error);
    }
  });
})();

(() => {
  document.querySelectorAll('.testimonials-1 .splide').forEach((item) => {
    const moduleEl = item.closest(moduleSelector);
    const slider = new Splide(item, {
      type: 'loop',
      perPage: 3,
      perMove: 1,
      gap: 30,
      pagination: true,
      arrows: false,
      autoplay: false,
      breakpoints: {
        1200: {
          perPage: 2,
          gap: 15,
        },
        768: {
          perPage: 1,
        },
      },
    });

    slider.on('mounted resized', () => {
      if (moduleEl) updateClampedState(moduleEl);
    });

    slider.mount();
  });

  setTimeout(() => {
    window.dispatchEvent(new Event('resize'));
  }, 500);
})();
